/**
 * PLAT-10916 — the four callbacks the quick-edit popover needs to run an AI
 * text row, built once for every surface that mounts it.
 *
 * Both clip-preview entry points — the transcript chip
 * (`ClipTranscriptBridge`) and the canvas Edit pill
 * (`CompositionCanvasActionsLayer`) — are separate component trees rendering
 * the SAME popover, and the promise the feature makes is that a graphic behaves
 * identically whichever one the creator used. Two hand-rolled copies of this
 * wiring is precisely how that promise breaks: PLAT-10915 shipped two, and
 * PLAT-10916 would have made it two copies of four callbacks each. The Creator
 * Studio quick-edit popover (`LeftSidePanel`, PLAT-10935) is the third caller,
 * and its canvas Edit pill reuses the second — the same
 * `CompositionCanvasActionsLayer` both studios mount.
 *
 * A plain function, not a hook: callers memoize it where their own inputs make
 * that worthwhile.
 */
import type { AITextTranscriptElement } from './useAITextBaseText';
import type { AITextGeneration } from './useAITextGeneration';
import type { CompositionQuickEditPopoverProps } from '@src/components/transcript/CompositionQuickEditPopover';
import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';

/**
 * Typed off the popover's own prop so the two can't drift — adding a callback
 * there without wiring it here becomes a type error rather than a surface that
 * quietly lacks the feature.
 */
export type AITextPopoverContext = NonNullable<
  CompositionQuickEditPopoverProps['aiTextContext']
>;

export function buildAITextPopoverContext(params: {
  aiTextGeneration: AITextGeneration;
  /** The clip's transcript, for `selection` and `clip` alike. */
  transcriptElements: ReadonlyArray<AITextTranscriptElement> | null | undefined;
  /**
   * The graphic this popover is editing. Its `id` IS the element every call
   * below addresses — the marker and the timeline element are the same thing.
   */
  overlay: TimelineMarker;
}): AITextPopoverContext {
  const { aiTextGeneration, transcriptElements, overlay } = params;
  const elementId = overlay.id;
  return {
    transcriptElements,
    overlay,
    generate: ({ field, baseTextScope, instructions }) =>
      aiTextGeneration.generateForField({
        elementId,
        field,
        baseTextScope,
        overlay,
        instructions,
      }),
    status: controlId => aiTextGeneration.getStatus(elementId, controlId),
    provenance: controlId =>
      aiTextGeneration.getProvenance(elementId, controlId),
    selectOption: ({ field, optionIndex }) =>
      aiTextGeneration.selectOptionForField({ elementId, field, optionIndex }),
    // PLAT-10923 — the identity for the two events the control fires itself.
    // Through here rather than assembled in the popover: the surface tag lives
    // on the generation, and every caller of this builder gets it for free.
    analytics: controlId => aiTextGeneration.analytics(elementId, controlId),
  };
}
